import { useEffect, useRef, type RefObject } from 'react';
import { useBackToClose } from '@/hooks/useBackToClose';
import { useScrollLock } from '@/hooks/useScrollLock';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * What every handheld overlay layer does, shared by `<Sheet>` and `<Drawer>`.
 *
 * The ARIA dialog pattern as the calendar's `Dialog` implements it — focus in
 * on open, Escape closes, Tab cycles inside, focus back to where it was on
 * close — plus the one thing a layer over a *scrolling document* adds: the
 * document is locked while the layer is up, so the layer's own scroller is the
 * only thing a finger can move (§5) -- and, on a phone, Back closes the layer
 * rather than leaving the page under it (`useBackToClose`).
 *
 * The keydown listener is bound imperatively for the same reason `Dialog`
 * gives: the panel is a focus container with a non-interactive role, not a
 * widget, and a JSX key handler on it is what `jsx-a11y` rightly flags.
 */
export function useOverlayLayer(
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
): void {
  useScrollLock(open);
  useBackToClose(open, onClose);

  // Read through a ref so a parent passing an inline `onClose` does not
  // re-run the focus effect (and steal focus back to the first item) on every
  // render while the layer is open.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel).focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const head = nodes[0];
      const tail = nodes[nodes.length - 1];
      if (!head || !tail) return;
      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    };
    panel.addEventListener('keydown', onKeyDown);
    return () => {
      panel.removeEventListener('keydown', onKeyDown);
      // A navigation can unmount the opener while the layer is up (the drawer
      // closes *because* a folder was picked); focusing a detached node would
      // drop focus to <body>, which is where it goes anyway.
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, panelRef]);
}
